import NProgress from 'nprogress';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';

import getLayerStore from '@stores/LayerStore';
import { removeMultipleArrayValuesByKey } from '@lib/utils';
import { CLUSTER_MAX_ZOOM, COORDINATES_MARGIN, MAP_LAYERS, ZOOM_LEVELS } from '@lib/const';
import { PropertyInfoPopup } from '@components/PropertyInfoPopup';
import { preparePropertiesDataForPopup } from './mockPropertiesData';
import { layerHoverCursor } from './shared';

import type {
  GetPropertiesByCoordinatesQueryVariables,
  GetPropertiesByCoordinates_Properties,
} from '@graphql';
import type { LatLngCoordinates } from '@lib/types';
import type { ApolloClient } from '@lib/apolloClient';
import type { GeoJSONSource, Map, MapboxGeoJSONFeature, Point, Popup } from 'mapbox-gl';
import type { ReactNode } from 'react';
import type { PropertyFeature } from './mockPropertiesData';

const LayerStore = getLayerStore();

export type GeoJSONPropertyFeature = MapboxGeoJSONFeature & PropertyFeature;

export function init(map: Map): void {
  map.addSource(MAP_LAYERS.properties, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
    cluster: true,
    clusterMaxZoom: CLUSTER_MAX_ZOOM,
  });

  map.addLayer({
    id: MAP_LAYERS.propertiesClusteredStroke,
    type: 'circle',
    source: MAP_LAYERS.properties,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#000',
      'circle-opacity': 0.18,
      'circle-radius': 25,
    },
  });

  map.addLayer({
    id: MAP_LAYERS.propertiesClusteredShadow,
    type: 'circle',
    source: MAP_LAYERS.properties,
    filter: ['has', 'point_count'],
    paint: {
      'circle-blur': 0.5,
      'circle-color': '#000',
      'circle-radius': 20,
      'circle-translate': [0, 2],
    },
  });

  map.addLayer({
    id: MAP_LAYERS.propertiesClustered,
    type: 'circle',
    source: MAP_LAYERS.properties,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#FFF',
      'circle-radius': 17,
    },
  });

  map.addLayer({
    id: MAP_LAYERS.propertiesClusteredCount,
    type: 'symbol',
    source: MAP_LAYERS.properties,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Roboto Black', 'DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-offset': [0, 0.15],
      'text-size': 12,
    },
  });

  map.addLayer({
    id: MAP_LAYERS.propertiesUnclusteredShadow,
    type: 'circle',
    source: MAP_LAYERS.properties,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-blur': 0.5,
      'circle-color': '#000',
      'circle-opacity': 0.5,
      'circle-radius': 15,
      'circle-translate': [0, 2],
    },
  });

  map.addLayer({
    id: MAP_LAYERS.propertiesUnclustered,
    type: 'circle',
    source: MAP_LAYERS.properties,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': '#757575',
      'circle-radius': 8,
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 4,
    },
  });

  layerHoverCursor(map, MAP_LAYERS.propertiesUnclustered);
  layerHoverCursor(map, MAP_LAYERS.propertiesClustered);
}

export function isPropertyLevel(mapbox: Map = window.Mapbox): boolean {
  return mapbox.getZoom() >= ZOOM_LEVELS.district;
}

export function showProperties(mapbox: Map = window.Mapbox): void {
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClusteredStroke, 'visibility', 'visible');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClusteredShadow, 'visibility', 'visible');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClustered, 'visibility', 'visible');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClusteredCount, 'visibility', 'visible');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesUnclusteredShadow, 'visibility', 'visible');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesUnclustered, 'visibility', 'visible');
}

export function hideProperties(mapbox: Map = window.Mapbox): void {
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClusteredStroke, 'visibility', 'none');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClusteredShadow, 'visibility', 'none');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClustered, 'visibility', 'none');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesClusteredCount, 'visibility', 'none');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesUnclusteredShadow, 'visibility', 'none');
  mapbox.setLayoutProperty(MAP_LAYERS.propertiesUnclustered, 'visibility', 'none');
}

export async function getProperties(
  apolloClient: ApolloClient,
  map: Map,
): Promise<PropertyFeature[]> {
  NProgress.start();
  const sw = map.getBounds().getSouthWest();
  const ne = map.getBounds().getNorthEast();

  const formattedBounds: GetPropertiesByCoordinatesQueryVariables = {
    bounds: {
      sw: {
        lat: sw.lat - COORDINATES_MARGIN,
        long: sw.lng - COORDINATES_MARGIN,
      },
      ne: {
        lat: ne.lat + COORDINATES_MARGIN,
        long: ne.lng + COORDINATES_MARGIN,
      },
    },
  };
  const data = await LayerStore.getPropertiesByCoordinates(apolloClient, formattedBounds);
  NProgress.done();
  let properties: PropertyFeature[] = [];
  if (data.properties && data.properties.properties) {
    properties = (
      removeMultipleArrayValuesByKey(
        data.properties.properties as [],
        'address',
      ) as GetPropertiesByCoordinates_Properties[]
    ).map((property) => ({
      type: 'Feature',
      properties: {
        address: property.address.split(',')[0] || '',
        etage: property.etage || 0,
        price: property.price || 0,
        priceSqM: property.priceSqM || 0,
        onlineSince: property.onlineSince || '',
        rooms: property.rooms || 0,
        sqM: property.sqM || 0,
        img: undefined,
      },
      geometry: {
        type: 'Point',
        coordinates: [property.lng, property.lat],
      },
    }));
  }

  return properties;
}

export function handleClusterClick(map: Map, point: Point): void {
  const features = featuresFromPoint(map, MAP_LAYERS.propertiesClustered, point);

  const clusterId = features[0].properties.cluster_id as number;

  (map.getSource(MAP_LAYERS.properties) as GeoJSONSource).getClusterExpansionZoom(
    clusterId,
    (err, zoom) => {
      if (err) {
        return;
      }

      map.jumpTo({
        center: features[0].geometry.coordinates as LatLngCoordinates,
        zoom: zoom + 1,
      });
    },
  );
}

const popupContainer = process.browser
  ? document.createElement('div')
  : (null as unknown as HTMLDivElement);

export async function updateProperties(
  apolloClient: ApolloClient,
  map: Map = window.Mapbox,
): Promise<void> {
  const properties = await getProperties(apolloClient, map);
  (map.getSource(MAP_LAYERS.properties) as mapboxgl.GeoJSONSource).setData({
    type: 'FeatureCollection',
    features: properties,
  });
}

export function handlePropertyClick(map: Map, point: Point, popup: Popup): void {
  const features = featuresFromPoint(map, MAP_LAYERS.propertiesUnclustered, point);
  LayerStore.setPositionSelected(true);

  const root = createRoot(popupContainer);
  root.render(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    createElement(PropertyInfoPopup, undefined, [
      preparePropertiesDataForPopup(features[0].properties) as unknown as ReactNode,
    ]) as any,
  );

  popup
    .setDOMContent(popupContainer)
    .setLngLat(features[0].geometry.coordinates.slice() as LatLngCoordinates)
    .addTo(map)
    .on('close', function () {
      LayerStore.setPositionSelected(false);
    });
}

function featuresFromPoint(map: Map, layer: string, point: Point): GeoJSONPropertyFeature[] {
  return map.queryRenderedFeatures(point, {
    layers: [layer],
  }) as GeoJSONPropertyFeature[];
}
